import { useEffect, useRef } from "react";

const stats = [
  { value: "3.2x", label: "Higher close rate vs. shared leads" },
  { value: "< 60s", label: "Average lead delivery time" },
  { value: "100%", label: "Exclusive — 1 lead, 1 installer" },
];

export default function WhyConvert() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".animate-in");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="why-convert" className="py-24 md:py-32 bg-[#F7FAF7]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left — text */}
          <div>
            <span className="text-green-600 text-xs font-bold uppercase tracking-widest mb-3 block animate-in"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}>
              Why Our Leads Convert
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-black tracking-tight leading-tight mb-6 animate-in"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}>
              Unlike traditional lead forms, our platform shows homeowners their{" "}
              <span className="text-green-600">actual savings</span>{" "}
              before they submit.
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-8 animate-in"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}>
              Most lead gen companies capture cold interest. We capture homeowners who have already seen their roof analysis, estimated panel placement, and projected monthly savings — then decided to take action.
            </p>

            {/* Benefits */}
            <div className="flex flex-col gap-4 animate-in"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}>
              {[
                { icon: "ri-arrow-up-line", label: "Higher intent", desc: "They already know the numbers — no cold education needed." },
                { icon: "ri-percent-line", label: "Better close rates", desc: "Warm leads close faster and with less friction." },
                { icon: "ri-money-dollar-circle-line", label: "Less wasted spend", desc: "Stop paying for leads that go nowhere." },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-green-600 text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-black text-base">{item.label}</p>
                    <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — stats + image */}
          <div className="flex flex-col gap-5 animate-in"
            style={{ opacity: 0, transform: "translateY(24px)", transition: "opacity 0.6s ease, transform 0.6s ease" }}>
            {/* Image */}
            <div className="w-full h-56 md:h-72 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/7a0d22356bf4efa7b54846ef7650cb56.jpg"
                alt="Homeowner viewing solar savings"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-white border border-gray-100 rounded-2xl p-5 text-center">
                  <p className="text-2xl md:text-3xl font-black text-green-600 leading-none mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs leading-snug">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
